/** Oracle's Huts and Quest Guards: the one map object that asks for something back.
 *
 * A quest lives entirely in the object's payload, and a *completed* quest lives
 * in the payload too, as a per-player mark. That is deliberate: a quest has to
 * outlive the hero that finished it - a Quest Guard opens for the kingdom, not
 * for the scout who happened to carry the artifact - and `HeroState.visited` is
 * a hero list. It is the same reasoning, and the same key shape, as the
 * Keymaster's Tent.
 *
 * ## Payload vocabulary
 *
 * A Oracle's Hut is a `bonus` with `service: "seer"`. What it asks for:
 *
 * | key             | meaning                                                |
 * |-----------------|--------------------------------------------------------|
 * | `quest`         | `artifact`, `creatures`, `resources`, `level`, `army`   |
 * | `questArtifact` | the relic to hand over                                  |
 * | `questCreature` + `questCount` | the stack to hand over                   |
 * | `questResource` + `questAmount` | the tribute to pay                      |
 * | `questLevel`    | the hero level to have reached                          |
 * | `questArmy`     | the army value, in `aiValue` points, to be leading      |
 * | `questChannel`  | the Quest Guard colour this hut unlocks, if any         |
 *
 * What it pays, on the shared vocabulary in `reward.ts`: `gold`, `experience`,
 * `artifact`, `rewardCreature` + `rewardCount`, `resource` + `amount`, `spell`,
 * `primary` + `primaryAmount`.
 *
 * A Quest Guard is a `gate` whose `colour` is `"quest"` and whose `quest` key
 * names a channel. It opens for a player who has finished any hut on that
 * channel, and `entryIllegality` in `objects.ts` is where that is enforced -
 * one door, the same one border gates go through.
 *
 * ## Why the toll is taken and not just checked
 *
 * `artifact`, `creatures` and `resources` are *deliveries*: the classic takes them.
 * `level` and `army` are *thresholds*: the classic only looks. Getting that backwards
 * either robs a player who merely walked past, or lets one hero clear every hut
 * on the map with the same relic, so the two families are separated here rather
 * than in the content.
 */

import type { HeroId, ObjectId, PlayerId } from "../ids.js";
import { asArtifactDefId, asCreatureDefId } from "./ids.js";
import type {
  HeroState, InteractionResponse, MapObjectState, ResourceKind,
} from "../state.js";
import { RESOURCES } from "../state.js";
import { entityKeys } from "../util/iter.js";
import type { Ctx } from "./ctx.js";
import { OK_CANCEL, addResource, patchHero, raise } from "./ctx.js";
import type { Payload, VisitOutcome } from "./payload.js";
import { DONE, payNum, payStr, payloadOf, setPayload } from "./payload.js";
import { armyValue, levelForExperience, maxMana, retuneMana } from "./hero.js";
import { withSlots } from "./slots.js";
import { grantReward } from "./reward.js";

// ------------------------------------------------------------------- marks

/**
 * "This player has finished this hut", in the hut's own payload.
 *
 * Distinct from the `p:<player>` visit mark, which only says somebody looked at
 * it: a hut a player has read but not yet paid must keep asking.
 */
export const questDoneKey = (p: PlayerId): string => `q:${p}`;

export const questDone = (c: Ctx, o: MapObjectState, p: PlayerId): boolean =>
  payloadOf(c, o)[questDoneKey(p)] === true;

// -------------------------------------------------------------- the quest

export type QuestKind = "artifact" | "creatures" | "resources" | "level" | "army" | "none";

export function questKind(pay: Payload): QuestKind {
  const k = payStr(pay, "quest");
  switch (k) {
    case "artifact": case "creatures": case "resources": case "level": case "army":
      return k;
    default:
      return "none";
  }
}

/** A delivery is taken when it is paid; a threshold is only looked at. */
export const isDelivery = (k: QuestKind): boolean =>
  k === "artifact" || k === "creatures" || k === "resources";

/**
 * The quest as one machine-readable line, for the prompt and for the journal.
 *
 * A string and not a struct because `InteractionPrompt.CHOOSE` carries strings
 * and `ResumeToken.data` carries scalars; the shell splits on the colons.
 */
export function questText(pay: Payload): string {
  const k = questKind(pay);
  switch (k) {
    case "artifact": return `quest:artifact:${payStr(pay, "questArtifact")}`;
    case "creatures":
      return `quest:creatures:${payStr(pay, "questCreature")}:${String(payNum(pay, "questCount", 0))}`;
    case "resources":
      return `quest:resources:${payStr(pay, "questResource")}:${String(payNum(pay, "questAmount", 0))}`;
    case "level": return `quest:level:${String(payNum(pay, "questLevel", 0))}`;
    case "army": return `quest:army:${String(payNum(pay, "questArmy", 0))}`;
    default: return "quest:none";
  }
}

/** Can this hero pay right now? Reads nothing and writes nothing. */
export function questSatisfied(c: Ctx, h: HeroState, pay: Payload): boolean {
  switch (questKind(pay)) {
    case "artifact": {
      const want = payStr(pay, "questArtifact");
      if (want === "") return false;
      return carriedArtifact(h, want);
    }
    case "creatures": {
      const def = payStr(pay, "questCreature");
      const n = payNum(pay, "questCount", 0);
      if (def === "" || n <= 0) return false;
      return carriedCreatures(c, h, def) >= n;
    }
    case "resources": {
      const r = payStr(pay, "questResource");
      const n = payNum(pay, "questAmount", 0);
      if (!RESOURCES.includes(r as ResourceKind) || n <= 0) return false;
      const pl = c.s.players[h.owner];
      return pl !== undefined && pl.resources[r as ResourceKind] >= n;
    }
    case "level":
      return levelForExperience(c.content, h.experience) >= payNum(pay, "questLevel", 0);
    case "army":
      return armyValue(c.content, c.s.armies[h.army] ?? null) >= payNum(pay, "questArmy", 0);
    default:
      // A hut with no quest is a hut that hands its reward to the first hero
      // through the door. That is a legal authoring - it is how a scenario
      // gives a gift - and it must not read as "unsatisfiable".
      return true;
  }
}

const carriedArtifact = (h: HeroState, want: string): boolean => {
  if (h.backpack.includes(asArtifactDefId(want))) return true;
  for (const k of Object.keys(h.artifacts).sort()) {
    if (h.artifacts[k] === asArtifactDefId(want)) return true;
  }
  return false;
};

function carriedCreatures(c: Ctx, h: HeroState, def: string): number {
  const army = c.s.armies[h.army];
  if (army === undefined) return 0;
  const id = asCreatureDefId(def);
  let n = 0;
  for (const s of army.slots) if (s !== null && s.def === id) n += s.count;
  return n;
}

// ------------------------------------------------------------------ visit

export function visitQuest(
  c: Ctx, heroId: HeroId, h: HeroState, o: MapObjectState, pay: Payload,
  path: readonly number[],
): VisitOutcome {
  if (questDone(c, o, h.owner)) {
    c.events.push({ type: "Message", player: h.owner, text: "seer:done" });
    return DONE;
  }
  if (!questSatisfied(c, h, pay)) {
    // The hut still says what it wants. A seer who says nothing is the same
    // silent no-op the empty payload was.
    c.events.push({ type: "Message", player: h.owner, text: questText(pay) });
    return DONE;
  }
  const kind = questKind(pay);
  if (!isDelivery(kind)) {
    // Nothing to hand over and nothing to decline: pay out where they stand.
    completeQuest(c, heroId, o, h.owner, pay);
    return DONE;
  }
  const raised = raise(c, h.owner,
    { kind: "CONFIRM", text: questText(pay) }, OK_CANCEL,
    { kind: "OBJECT", data: { sub: "SVC_seer", hero: heroId, object: o.id, path: [...path] } });
  if (!raised) {
    // One prompt at a time. Say so rather than taking the relic unasked.
    c.events.push({ type: "Message", player: h.owner, text: "seer:busy" });
    return DONE;
  }
  return { kind: "pending" };
}

export function resumeQuest(
  c: Ctx, heroId: HeroId, h: HeroState, o: MapObjectState, pay: Payload,
  response: InteractionResponse,
): void {
  if (response.kind !== "OK") return;
  if (questDone(c, o, h.owner)) return;
  // Re-checked rather than trusted: a prompt survives across commands, and the
  // relic can have been handed to another hero between the question and the
  // answer.
  if (!questSatisfied(c, h, pay)) {
    c.events.push({ type: "Message", player: h.owner, text: questText(pay) });
    return;
  }
  takeToll(c, h, pay);
  completeQuest(c, heroId, o, h.owner, pay);
}

/** Deliveries leave the hero's hands. Thresholds do not. */
function takeToll(c: Ctx, h: HeroState, pay: Payload): void {
  switch (questKind(pay)) {
    case "artifact": {
      const want = asArtifactDefId(payStr(pay, "questArtifact"));
      const cur = c.s.heroes[h.id];
      if (cur === undefined) return;
      const bagAt = cur.backpack.indexOf(want);
      if (bagAt >= 0) {
        patchHero(c, h.id, { backpack: cur.backpack.filter((_, i) => i !== bagAt) });
        return;
      }
      for (const k of Object.keys(cur.artifacts).sort()) {
        if (cur.artifacts[k] === want) {
          // Off the doll, so the same state change as UNEQUIP_ARTIFACT: a
          // relic that carried Knowledge takes its share of the pool with it.
          const prevMax = maxMana(c, cur);
          patchHero(c, h.id, { artifacts: withSlots(cur, { [k]: null }) });
          retuneMana(c, h.id, prevMax);
          return;
        }
      }
      return;
    }
    case "creatures": {
      const def = asCreatureDefId(payStr(pay, "questCreature"));
      let left = payNum(pay, "questCount", 0);
      const army = c.s.armies[h.army];
      if (army === undefined) return;
      const slots = army.slots.map((s) => {
        if (left <= 0 || s === null || s.def !== def) return s;
        const take = s.count <= left ? s.count : left;
        left -= take;
        return s.count - take > 0 ? { def, count: s.count - take } : null;
      });
      c.s.armies = { ...c.s.armies, [h.army]: { ...army, slots } };
      return;
    }
    case "resources": {
      const r = payStr(pay, "questResource") as ResourceKind;
      const n = payNum(pay, "questAmount", 0);
      if (RESOURCES.includes(r) && n > 0) addResource(c, h.owner, r, -n);
      return;
    }
    default:
      return;
  }
}

function completeQuest(
  c: Ctx, heroId: HeroId, o: MapObjectState, p: PlayerId, pay: Payload,
): void {
  setPayload(c, o.id, { [questDoneKey(p)]: true });
  c.events.push({ type: "Message", player: p, text: `seer:paid:${payStr(pay, "questChannel")}` });
  grantReward(c, heroId, pay);
}

// ------------------------------------------------------------ quest gates

/**
 * A Quest Guard opens for a kingdom that has finished the hut it is tied to.
 *
 * A gate whose `quest` channel names no hut on the map is *open*, not shut: a
 * generator that lays a guard and fails to lay its hut has walled off a piece
 * of the map for ever, and an unreachable region is a worse bug than a gate
 * that swings freely. A gate whose channel names a hut nobody has finished is
 * shut, which is the ordinary case.
 */
export function questGateOpen(c: Ctx, p: PlayerId, channel: string): boolean {
  if (channel === "") return true;
  let found = false;
  for (const id of entityKeys(c.s.objects)) {
    const o = c.s.objects[id as ObjectId]!;
    if (o.removed) continue;
    const pay = payloadOf(c, o);
    if (payStr(pay, "service") !== "seer") continue;
    if (payStr(pay, "questChannel") !== channel) continue;
    found = true;
    if (pay[questDoneKey(p)] === true) return true;
  }
  return !found;
}
